import React, { useState, useEffect, useRef } from 'react';
import { supabaseClient } from '../../services/supabase';

// 画像リサイズ関数
const resizeImage = (file, maxWidth = 1600, maxHeight = 2400, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas to Blob failed'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

function MenuManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    menu_type: 'food',
    title: '',
    is_visible: true,
    imageFile: null,
    imagePreview: null
  });
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    loadItems();
  }, []);

  // モーダルが開いたときに自動スクロール
  useEffect(() => {
    if (isModalOpen && modalRef.current) {
      setTimeout(() => {
        modalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 100);
    }
  }, [isModalOpen]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseClient
        .from('menu_images')
        .select('*')
        .order('menu_type', { ascending: true });

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('読み込みエラー:', error);
      alert('データの読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        menu_type: item.menu_type || 'food',
        title: item.title || '',
        is_visible: item.is_visible !== false,
        imageFile: null,
        imagePreview: item.image_url
      });
    } else {
      setEditingItem(null);
      setFormData({
        menu_type: 'food',
        title: '',
        is_visible: true,
        imageFile: null,
        imagePreview: null
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormData({
      menu_type: 'food',
      title: '',
      is_visible: true,
      imageFile: null,
      imagePreview: null
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください。');
      return;
    }

    // ファイルサイズチェック（10MB以下）
    if (file.size > 10 * 1024 * 1024) {
      alert('ファイルサイズは10MB以下にしてください。');
      return;
    }

    // プレビュー表示
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        imagePreview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert('タイトルを入力してください。');
      return;
    }
    if (!editingItem && !formData.imageFile) {
      alert('画像を選択してください。');
      return;
    }

    setUploading(true);
    try {
      let imageUrl = editingItem?.image_url;

      if (formData.imageFile) {
        // 画像をリサイズ
        const resizedBlob = await resizeImage(formData.imageFile);
        const fileName = `menu_${formData.menu_type}_${Date.now()}.jpg`;
        const { error: uploadError } = await supabaseClient.storage
          .from('menu-images')
          .upload(fileName, resizedBlob, {
            contentType: 'image/jpeg',
            upsert: true
          });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabaseClient.storage
          .from('menu-images')
          .getPublicUrl(fileName);

        imageUrl = urlData.publicUrl;

        if (editingItem?.image_url) {
          const oldFileName = editingItem.image_url.split('/').pop();
          await supabaseClient.storage
            .from('menu-images')
            .remove([oldFileName]);
        }
      }

      const itemData = {
        menu_type: formData.menu_type,
        title: formData.title.trim(),
        image_url: imageUrl,
        is_visible: formData.is_visible,
        updated_at: new Date().toISOString()
      };

      if (editingItem) {
        const { error } = await supabaseClient
          .from('menu_images')
          .update(itemData)
          .eq('id', editingItem.id);

        if (error) throw error;
        alert('更新しました。');
      } else {
        const { error } = await supabaseClient
          .from('menu_images')
          .insert(itemData);

        if (error) throw error;
        alert('作成しました。');
      }

      closeModal();
      loadItems();
    } catch (error) {
      console.error('保存エラー:', error);
      alert('保存に失敗しました。\n' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`「${item.title}」を削除しますか？`)) {
      return;
    }

    try {
      const { error } = await supabaseClient
        .from('menu_images')
        .delete()
        .eq('id', item.id);

      if (error) throw error;

      if (item.image_url) {
        const fileName = item.image_url.split('/').pop();
        await supabaseClient.storage
          .from('menu-images')
          .remove([fileName]);
      }

      alert('削除しました。');
      loadItems();
    } catch (error) {
      console.error('削除エラー:', error);
      alert('削除に失敗しました。');
    }
  };

  const handleToggleVisibility = async (item) => {
    try {
      const { error } = await supabaseClient
        .from('menu_images')
        .update({ is_visible: !item.is_visible })
        .eq('id', item.id);

      if (error) throw error;
      loadItems();
    } catch (error) {
      console.error('更新エラー:', error);
      alert('更新に失敗しました。');
    }
  };

  const foodItems = items.filter(item => item.menu_type === 'food');
  const drinkItems = items.filter(item => item.menu_type === 'drink');

  return (
    <section className="admin-section">
      <div className="container">
        <h2>メニュー画像管理</h2>

        <div className="action-bar" style={{ marginBottom: '20px' }}>
          <button className="btn-primary" onClick={() => openModal()}>
            + 新規追加
          </button>
        </div>

        <p style={{ color: '#888', marginBottom: '20px' }}>
          お食事メニューとドリンクメニューの画像を管理します。
        </p>

        {loading ? (
          <p>読み込み中...</p>
        ) : (
          <>
            <div className="menu-category-section">
              <h3 className="menu-category-title">🍣 お食事メニュー</h3>
              {foodItems.length === 0 ? (
                <p style={{ color: '#888' }}>お食事メニューがありません。</p>
              ) : (
                <div className="menu-admin-grid">
                  {foodItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={`menu-admin-card ${!item.is_visible ? 'hidden-item' : ''}`}
                    >
                      <div className="menu-admin-image">
                        <img src={item.image_url} alt={item.title} />
                        {!item.is_visible && (
                          <div className="hidden-badge">非表示</div>
                        )}
                      </div>
                      <div className="menu-admin-info">
                        <p className="menu-admin-title">{item.title}</p>
                      </div>
                      <div className="menu-admin-actions">
                        <button 
                          className={`btn-small ${item.is_visible ? 'btn-toggle' : 'btn-show'}`}
                          onClick={() => handleToggleVisibility(item)}
                        >
                          {item.is_visible ? '非表示' : '表示'}
                        </button>
                        <button 
                          className="btn-small btn-edit"
                          onClick={() => openModal(item)}
                        >
                          編集
                        </button>
                        <button 
                          className="btn-small btn-delete"
                          onClick={() => handleDelete(item)}
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="menu-category-section">
              <h3 className="menu-category-title">🍶 ドリンクメニュー</h3>
              {drinkItems.length === 0 ? (
                <p style={{ color: '#888' }}>ドリンクメニューがありません。</p>
              ) : (
                <div className="menu-admin-grid">
                  {drinkItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={`menu-admin-card ${!item.is_visible ? 'hidden-item' : ''}`}
                    >
                      <div className="menu-admin-image">
                        <img src={item.image_url} alt={item.title} />
                        {!item.is_visible && (
                          <div className="hidden-badge">非表示</div>
                        )}
                      </div>
                      <div className="menu-admin-info">
                        <p className="menu-admin-title">{item.title}</p>
                      </div>
                      <div className="menu-admin-actions">
                        <button 
                          className={`btn-small ${item.is_visible ? 'btn-toggle' : 'btn-show'}`}
                          onClick={() => handleToggleVisibility(item)}
                        >
                          {item.is_visible ? '非表示' : '表示'}
                        </button>
                        <button 
                          className="btn-small btn-edit"
                          onClick={() => openModal(item)}
                        >
                          編集
                        </button>
                        <button 
                          className="btn-small btn-delete"
                          onClick={() => handleDelete(item)}
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* 編集モーダル */}
        {isModalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" ref={modalRef} onClick={(e) => e.stopPropagation()}>
              <h3>{editingItem ? 'メニュー編集' : '新規メニュー追加'}</h3>
              
              <div className="form-group">
                <label>メニュータイプ *</label>
                <select
                  value={formData.menu_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, menu_type: e.target.value }))}
                >
                  <option value="food">お食事メニュー</option>
                  <option value="drink">ドリンクメニュー</option>
                </select>
              </div>

              <div className="form-group">
                <label>タイトル *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="例: お食事"
                />
              </div>

              <div className="form-group">
                <label>画像 *</label>
                <div className="image-upload-area">
                  {formData.imagePreview ? (
                    <div className="image-preview">
                      <img src={formData.imagePreview} alt="プレビュー" />
                      <button 
                        type="button" 
                        className="btn-change-image"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        画像を変更
                      </button>
                    </div>
                  ) : (
                    <div 
                      className="image-dropzone"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <p>クリックして画像を選択</p>
                      <p className="hint">JPEG, PNG, WebP（最大10MB）</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                </div>
                <p className="form-hint">※アップロード時に自動でリサイズされます（最大1600×2400px）</p>
              </div>

              <div className="form-group">
                <label>表示設定</label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_visible}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_visible: e.target.checked }))}
                  />
                  サイトに表示する
                </label>
              </div>

              <div className="modal-actions">
                <button 
                  className="btn-secondary" 
                  onClick={closeModal}
                  disabled={uploading}
                >
                  キャンセル
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleSave}
                  disabled={uploading}
                >
                  {uploading ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .menu-category-section {
          margin-bottom: 40px;
        }

        .menu-category-title {
          color: #ffffff;
          font-size: 1.2rem;
          margin-bottom: 15px;
          padding-bottom: 10px;
          border-bottom: 1px solid #333;
        }

        .menu-admin-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }

        .menu-admin-card {
          background: #1a1a1a;
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #333;
        }

        .menu-admin-card.hidden-item {
          opacity: 0.6;
        }

        .menu-admin-image {
          position: relative;
          width: 100%;
          height: 200px;
          overflow: hidden;
        }

        .menu-admin-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .hidden-badge {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(100, 100, 100, 0.9);
          color: white;
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: bold;
        }

        .menu-admin-info {
          padding: 15px;
        }

        .menu-admin-title {
          color: #ffffff;
          font-weight: bold;
          margin-bottom: 5px;
        }

        .menu-admin-actions {
          display: flex;
          gap: 8px;
          padding: 15px;
          border-top: 1px solid #333;
          flex-wrap: wrap;
        }

        .btn-small {
          padding: 6px 12px;
          font-size: 0.85rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          min-width: 60px;
        }

        .btn-toggle {
          background: #555;
          color: white;
        }

        .btn-show {
          background: #28a745;
          color: white;
        }

        .btn-edit {
          background: #2196F3;
          color: white;
        }

        .btn-delete {
          background: #dc3545;
          color: white;
        }

        .image-upload-area {
          margin-top: 10px;
        }

        .image-preview {
          position: relative;
          max-width: 300px;
        }

        .image-preview img {
          width: 100%;
          border-radius: 8px;
        }

        .btn-change-image {
          display: block;
          margin-top: 10px;
          padding: 8px 16px;
          background: #2196F3;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
        }

        .btn-change-image:hover {
          background: #1976D2;
        }

        .image-dropzone {
          border: 2px dashed #444;
          border-radius: 8px;
          padding: 40px;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.3s ease;
        }

        .image-dropzone:hover {
          border-color: #666;
        }

        .image-dropzone p {
          color: #888;
          margin: 5px 0;
        }

        .image-dropzone .hint {
          font-size: 0.85rem;
          color: #666;
        }

        .form-hint {
          font-size: 0.8rem;
          color: #888;
          margin-top: 5px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #ffffff;
          cursor: pointer;
        }

        .checkbox-label input {
          width: 18px;
          height: 18px;
        }
      `}</style>
    </section>
  );
}

export default MenuManagement;
